import Vue from 'vue';
import axios from 'axios';
import config from '../../config';
import { client } from '@/services';
import { getSelectedVenue, isValidDate } from '@/services/helpers';
import { USER_ROLES, PLAYLIST_STATUS } from '@/constants';
import { generateCopyName } from '../../services/helpers';
import apiClient from '@/services/apiClient';

const playlistsService = client.service('playlists');
const assetsService = client.service('assets');

function removeColons(val) {
  return +val.replace(/:/g, '');
}

function getMimeType(dataUri) {
  return dataUri.match(/[^:]\w+\/[\w-+\d.]+(?=;|,)/)[0];
}

const playlists = {
  namespaced: true,
  state: {
    playlists: [],
    paginatedPlaylists: [],
    paginatedTotal: 0,
    paginatedLoading: false
  },
  getters: {
    GET_PLAYLISTS: state => [...state.playlists].sort((a, b) => a.name.localeCompare(b.name)),
    GET_PAGINATED_PLAYLISTS: state => state.paginatedPlaylists,
    GET_PAGINATED_TOTAL: state => state.paginatedTotal,
    GET_PAGINATED_LOADING: state => state.paginatedLoading
  },
  mutations: {
    SET_PLAYLISTS(state, playlists) {
      state.playlists = playlists;
    },
    ADD_PLAYLIST(state, playlist) {
      state.playlists.push(playlist);
    },
    UPDATE_PLAYLIST(state, updatedPlaylist) {
      const index = state.playlists.findIndex(playlist => playlist._id === updatedPlaylist._id);
      if (index >= 0) {
        Vue.set(state.playlists, index, updatedPlaylist);
      }
    },
    REMOVE_PLAYLIST(state, _id) {
      const index = state.playlists.findIndex(playlist => playlist._id === _id);
      if (index >= 0) {
        state.playlists.splice(index, 1);
      }
    },
    CLEAR_PLAYLISTS(state) {
      state.playlists.length = 0;
    },

    SET_PAGINATED_PLAYLISTS(state, { data, total }) {
      state.paginatedPlaylists = data;
      state.paginatedTotal = total;
    },
    SET_PAGINATED_LOADING(state, loading) {
      state.paginatedLoading = loading;
    },
    CLEAR_PAGINATED_PLAYLISTS(state) {
      state.paginatedPlaylists = [];
      state.paginatedTotal = 0;
    }
  },
  actions: {
    async GET_PLAYLISTS({ commit, rootState }) {
      const playlists = await playlistsService.find({
        query: {
          venueId: getSelectedVenue()._id,
          $paginate: { value: false }
        }
      });
      commit('SET_PLAYLISTS', playlists);
      return playlists;
    },
    async GET_PLAYLISTS_BY_NAME(_, playlistName) {
      const playlists = await playlistsService.find({
        query: {
          venueId: getSelectedVenue()._id,
          $paginate: { value: false }
        }
      });
      return playlists
        .filter(playlist => playlist.name.toLowerCase().includes(playlistName.toLowerCase()))
        .map(playlist => playlist.name);
    },
    async GET_PLAYLIST(_, _id) {
      try {
        const playlist = await playlistsService.get(_id);
        for (let item of playlist.items) {
          if (!item.startDate) item.startDate = '';
          if (!item.endDate) item.endDate = '';
        }
        return playlist;
      } catch (e) {
        throw e;
      }
    },
    ADD_PLAYLIST({ rootState }, payload) {
      const playlist = JSON.parse(JSON.stringify(payload));
      const userId = rootState.users.user._id;
      const defaultScheduleDate = [
        { day: 'Sun', start_time: '00:00', end_time: '00:00' },
        { day: 'Mon', start_time: '00:00', end_time: '00:00' },
        { day: 'Tue', start_time: '00:00', end_time: '00:00' },
        { day: 'Wed', start_time: '00:00', end_time: '00:00' },
        { day: 'Thu', start_time: '00:00', end_time: '00:00' },
        { day: 'Fri', start_time: '00:00', end_time: '00:00' },
        { day: 'Sat', start_time: '00:00', end_time: '00:00' }
      ];
      playlist.status = PLAYLIST_STATUS.DRAFT;
      playlist.created_at = Date.now();
      playlist.userId = userId;
      playlist.venueId = getSelectedVenue()._id;
      playlist.totalSize = playlist.items.reduce((a, b) => +a + +b.size, 0);
      playlist.totalDuration = playlist.items.reduce((a, b) => {
        return a + Number(b.transitionDuration) + Number(b.displayDuration);
      }, 0);
      playlist.items = playlist.items.map(item => ({
        key: item.key,
        fill: item.fill,
        displayDuration: item.displayDuration,
        transition: item.transition,
        transitionDuration: item.transitionDuration,
        isRepeatAsset: item.isRepeatAsset,
        repeatAssetNumber: item.repeatAssetNumber,
        orientation: item.orientation,
        assetId: item._id,
        assetAdded: item.assetAdded,
        unique: item.unique,
        startDate: item.startDate,
        endDate: item.endDate,
        startTime: item.startTime,
        endTime: item.endTime,
        deleteOnEndDate: item.deleteOnEndDate,
        isAdvancedSchedule: item.isAdvancedSchedule || false,
        advancedScheduleDays: item.advancedScheduleDays || defaultScheduleDate
      }));
      return playlistsService.create(playlist);
    },
    async ADD_PLAYLIST_DUPLICATE({ rootState, dispatch }, payload) {
      try {
        const sameNameList = await dispatch('GET_PLAYLISTS_BY_NAME', payload.name);
        delete payload._id;
        payload.name = generateCopyName(payload.name, sameNameList);
        return await playlistsService.create(payload);
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_PLAYLIST({ commit, rootState }, payload) {
      try {
        const playlist = JSON.parse(JSON.stringify(payload.playlist));
        const defaultScheduleDate = [
          { day: 'Sun', start_time: '00:00', end_time: '00:00' },
          { day: 'Mon', start_time: '00:00', end_time: '00:00' },
          { day: 'Tue', start_time: '00:00', end_time: '00:00' },
          { day: 'Wed', start_time: '00:00', end_time: '00:00' },
          { day: 'Thu', start_time: '00:00', end_time: '00:00' },
          { day: 'Fri', start_time: '00:00', end_time: '00:00' },
          { day: 'Sat', start_time: '00:00', end_time: '00:00' }
        ];
        playlist.updated_at = Date.now();
        playlist.userId = rootState.users.user._id;
        playlist.venueId = getSelectedVenue()._id;
        playlist.totalSize = playlist.items.reduce(
          (a, b) => a + ((b.assetData && +b.assetData.size) || 0),
          0
        );
        playlist.totalDuration = playlist.items.reduce((a, b) => {
          return a + Number(b.transitionDuration) + Number(b.displayDuration);
        }, 0);
        playlist.items = playlist.items.map(item => {
          let newItem = {};
          if (!playlist.autogenerated) {
            newItem = {
              key: item.key,
              fill: item.fill,
              displayDuration: item.displayDuration,
              transition: item.transition,
              transitionDuration: item.transitionDuration,
              isRepeatAsset: item.isRepeatAsset,
              repeatAssetNumber: item.repeatAssetNumber,
              orientation: item.orientation,
              assetId: item.assetId,
              assetAdded: item.assetAdded,
              unique: item.unique,
              startDate: item.startDate,
              endDate: item.endDate,
              startTime: item.startTime,
              endTime: item.endTime,
              deleteOnEndDate: item.deleteOnEndDate,
              isAdvancedSchedule: item.isAdvancedSchedule || false,
              advancedScheduleDays: item.advancedScheduleDays || defaultScheduleDate
            };
          } else {
            delete item.assetData;
            newItem = item;
            delete newItem.assetId;
            newItem.image = item.image;
          }
          return newItem;
        });
        playlist.status = payload.status;
        const updated = await playlistsService.update(playlist._id, playlist);
        commit('UPDATE_PLAYLIST', updated);
        return updated;
      } catch (error) {
        throw error;
      }
    },
    async UPDATE_PLAYLIST_STATUS({ commit }, payload) {
      try {
        const playlist = JSON.parse(JSON.stringify(payload.playlist));
        playlist.updated_at = Date.now();
        playlist.status = payload.status;
        const patched = await playlistsService.patch(playlist._id, playlist);
        commit('UPDATE_PLAYLIST', patched);
        return patched;
      } catch (error) {
        throw error;
      }
    },
    async DELETE_PLAYLISTS({ commit, dispatch }, playlists) {
      try {
        const statuses = [];
        for (let x of playlists) {
          const response = await dispatch('schedules/SEARCH_PLAYLIST', x._id, { root: true });
          if (response.length > 0) {
            statuses.push({ status: 'error', playlist: x, response });
          } else {
            statuses.push({ status: 'success', playlist: x });
            await playlistsService.remove(x._id);
            commit('REMOVE_PLAYLIST', x._id);
          }
        }
        return statuses;
      } catch (e) {
        throw e;
      }
    },
    async SEARCH_ASSET({ commit }, _id) {
      return (await playlistsService.find({ query: { $search: _id } })).data;
    },
    async GET_PLAYLIST_DEFAULTS({}) {
      const defaults = await axios.get(config.server_url + '/api/settings/cms-client-config');
      return JSON.parse(defaults.data.config);
    },

    async FETCH_PLAYLISTS_PAGINATED(
      { commit },
      { pageIndex = 1, pageSize = 10, search = '', sortBy = null, descending = false } = {}
    ) {
      commit('SET_PAGINATED_LOADING', true);
      try {
        const $skip = (pageIndex - 1) * pageSize;
        const params = {
          $skip,
          $limit: pageSize,
          venueId: getSelectedVenue()._id
        };
        if (search) params.$search = search;

        if (sortBy) {
          const fieldMap = {
            name: 'name',
            totalSize: 'total_size',
            totalDuration: 'total_duration',
            status: 'status'
          };
          const dbField = fieldMap[sortBy] || sortBy;
          params[`$sort[${dbField}]`] = descending ? -1 : 1;
        } else {
          params['$sort[created_at]'] = -1;
        }

        const { data } = await apiClient.get('/playlists', { params });
        commit('SET_PAGINATED_PLAYLISTS', { data: data.data, total: data.total });
        return data;
      } catch (error) {
        throw error;
      } finally {
        commit('SET_PAGINATED_LOADING', false);
      }
    }
  }
};

export default playlists;



// WEBPACK FOOTER //
// ./src/store/modules/playlists.js